import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../apiService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const user = await apiService.getCurrentUser();
                setCurrentUser(user);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch current user:', err);
                setError(err.message);
                setCurrentUser(null);
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we have a token
        const token = apiService.getAuthToken();
        if (token) {
            fetchCurrentUser();
        } else {
            setLoading(false);
        }
    }, []);

    /**
     * Check if current user has any of the required roles
     * @param {string[]} requiredRoles - Array of role strings
     * @returns {boolean}
     */
    const hasPermission = (requiredRoles) => {
        if (!currentUser || !requiredRoles) return false;
        return requiredRoles.includes(currentUser.role);
    };

    /**
     * Check if user is superadmin
     * @returns {boolean}
     */
    const isSuperAdmin = () => {
        return currentUser?.role === 'superadmin';
    };

    /**
     * Check if user is org admin
     * @returns {boolean}
     */
    const isOrgAdmin = () => {
        return currentUser?.role === 'org_admin';
    };

    /**
     * Check if user is doctor
     * @returns {boolean}
     */
    const isDoctor = () => {
        return currentUser?.role === 'doctor';
    };

    /**
     * Check if user is nurse
     * @returns {boolean}
     */
    const isNurse = () => {
        return currentUser?.role === 'nurse';
    };

    /**
     * Check if user is receptionist
     * @returns {boolean}
     */
    const isReceptionist = () => {
        return currentUser?.role === 'receptionist';
    };

    /**
     * Check if user is pharmacist
     * @returns {boolean}
     */
    const isPharmacist = () => {
        return currentUser?.role === 'pharmacist';
    };

    /**
     * Refresh current user data
     */
    const refreshUser = async () => {
        setLoading(true);
        try {
            const user = await apiService.getCurrentUser();
            setCurrentUser(user);
            setError(null);
        } catch (err) {
            console.error('Failed to refresh user:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Clear user data (for logout)
     */
    const clearUser = () => {
        setCurrentUser(null);
        setError(null);
    };

    const value = {
        currentUser,
        loading,
        error,
        hasPermission,
        isSuperAdmin,
        isOrgAdmin,
        isDoctor,
        isNurse,
        isReceptionist,
        isPharmacist,
        refreshUser,
        clearUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * Role-based permission configurations
 * Used for consistent permission checking across the app
 */
export const PERMISSIONS = {
    // Patient Management
    VIEW_PATIENTS: ['superadmin', 'org_admin', 'doctor', 'nurse', 'receptionist', 'pharmacist'],
    ADD_PATIENTS: ['superadmin', 'org_admin', 'receptionist'],
    UPDATE_PATIENTS: ['superadmin', 'org_admin', 'doctor', 'nurse'],
    DELETE_PATIENTS: ['superadmin', 'org_admin', 'receptionist'],
    
    // Appointment Management
    VIEW_APPOINTMENTS: ['superadmin', 'org_admin', 'doctor', 'nurse', 'receptionist'],
    ADD_APPOINTMENTS: ['superadmin', 'org_admin', 'doctor', 'nurse', 'receptionist'], // Nurses can create appointments
    UPDATE_APPOINTMENTS: ['superadmin', 'org_admin', 'doctor', 'nurse', 'receptionist'], // Nurses can update appointments
    DELETE_APPOINTMENTS: ['superadmin', 'org_admin', 'receptionist'],
    
    // Staff Management
    VIEW_STAFF: ['superadmin', 'org_admin', 'doctor', 'nurse', 'receptionist', 'pharmacist'],
    ADD_STAFF: ['superadmin', 'org_admin'],
    UPDATE_STAFF: ['superadmin', 'org_admin'],
    DELETE_STAFF: ['superadmin', 'org_admin'],
    VIEW_STAFF_DETAILS: ['superadmin', 'org_admin'], // Only admins can view detailed staff info
    
    // Inventory Management
    VIEW_INVENTORY: ['superadmin', 'org_admin', 'doctor', 'nurse', 'receptionist', 'pharmacist'],
    ADD_INVENTORY: ['superadmin', 'org_admin', 'nurse', 'pharmacist'],
    UPDATE_INVENTORY: ['superadmin', 'org_admin', 'nurse', 'pharmacist'],
    DELETE_INVENTORY: ['superadmin', 'org_admin'],
    
    // Medical Records - CRITICAL HIPAA COMPLIANCE: Only clinical staff
    VIEW_MEDICAL_RECORDS: ['superadmin', 'org_admin', 'doctor', 'nurse'], // Receptionists REMOVED
    ADD_MEDICAL_RECORDS: ['superadmin', 'doctor', 'nurse'], // Nurses can create records (triage notes, vitals)
    UPDATE_MEDICAL_RECORDS: ['superadmin', 'doctor', 'nurse'], // Nurses can update their notes
    DELETE_MEDICAL_RECORDS: ['superadmin', 'doctor'], // Only doctors can delete
    
    // POS/Transactions
    VIEW_TRANSACTIONS: ['superadmin', 'org_admin', 'receptionist', 'pharmacist'],
    CREATE_TRANSACTIONS: ['superadmin', 'org_admin', 'receptionist', 'pharmacist'],
    
    // Reports
    VIEW_REPORTS: ['superadmin', 'org_admin'],
    
    // Settings
    VIEW_SETTINGS: ['superadmin', 'org_admin', 'doctor', 'nurse', 'receptionist', 'pharmacist'],
    UPDATE_SETTINGS: ['superadmin', 'org_admin']
};

export default AuthContext;

